import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/StatusBadge";
import ReviewDialog from "@/components/ReviewDialog";
import ClockInDialog from "@/components/ClockInDialog";
import { Calendar, Clock, MapPin, ChatCircle, Share, Timer } from "@phosphor-icons/react";
import { devWarn } from "@/lib/logger";

export default function MyBookings() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/bookings").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const cancel = async (id) => {
    if (!window.confirm("Cancel this booking?")) return;
    try { await api.delete(`/bookings/${id}`); toast.success("Booking cancelled"); load(); }
    catch { toast.error("Could not cancel"); }
  };
  const today = new Date().toISOString().slice(0,10);
  const share = async (b) => {
    const url = `${window.location.origin}/j/${b.job_id}`;
    if (navigator.share) {
      try { await navigator.share({ title: b.job?.title, text: `Shift on ${b.job_date}`, url }); return; }
      catch (err) { devWarn("navigator.share cancelled", err); /* fall through to clipboard */ }
    }
    navigator.clipboard.writeText(url); toast.success("Public link copied");
  };
  return (
    <div className="space-y-4">
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">My bookings</h1>
      {items.length === 0 && <div className="text-slate-500 py-10 text-center">No bookings yet. Find a shift to get started.</div>}
      <div className="space-y-3">
        {items.map((b) => (
          <div key={b.id} data-testid={`booking-${b.id}`} className="card-tactile p-5 space-y-3">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="label-caps text-blue-600 mb-1">{b.job?.category || "Shift"}</div>
                <h3 className="font-display text-xl font-bold text-slate-900">{b.job?.title}</h3>
                <div className="text-slate-600">{b.job?.company_name}</div>
                <div className="mt-2 flex flex-wrap gap-3 text-sm text-slate-700">
                  <span className="flex items-center gap-1.5"><Calendar size={14}/> {b.job_date}</span>
                  <span className="flex items-center gap-1.5"><Clock size={14}/> {b.start_time}–{b.end_time}</span>
                  <span className="flex items-center gap-1.5"><MapPin size={14}/> {b.job?.location}</span>
                  {b.clock_in_at && <span className="flex items-center gap-1.5 text-emerald-700"><Timer size={14}/> Clocked in {new Date(b.clock_in_at).toLocaleTimeString()}</span>}
                </div>
              </div>
              <div className="text-right">
                <StatusBadge status={b.status} />
                <div className="mt-2 font-display text-lg font-extrabold text-slate-900">£{b.net_amount}</div>
                <div className="label-caps">net</div>
              </div>
            </div>
            {b.status === "confirmed" && b.job_date === today && !b.clock_in_at && (
              <ClockInDialog booking={b} onDone={load} />
            )}
            <div className="pt-3 border-t border-slate-100 flex flex-wrap items-center gap-2">
              {b.status === "confirmed" && (
                <Button data-testid={`cancel-${b.id}`} variant="outline" size="sm" onClick={() => cancel(b.id)} className="text-red-600 border-red-200 hover:bg-red-50">Cancel booking</Button>
              )}
              {b.status === "confirmed" && !b.swap_status && (
                <Button data-testid={`swap-offer-${b.id}`} variant="outline" size="sm" onClick={async () => {
                  const reason = window.prompt("Reason for swap (optional):") || "";
                  try { await api.post(`/bookings/${b.id}/offer-swap`, { reason }); toast.success("Offered for swap"); load(); }
                  catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                }}>Offer swap</Button>
              )}
              {b.swap_status === "offered" && <span className="text-xs font-semibold uppercase tracking-wider text-yellow-700 bg-yellow-100 px-2 py-1 rounded-md">Offered for swap</span>}
              {b.status === "completed" && <ReviewDialog booking={b} onDone={load} />}
              <Button size="sm" variant="outline" onClick={() => share(b)} data-testid={`share-${b.id}`}><Share size={14} className="mr-1"/> Share</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
